import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { t } from '../../translations';
import '../../components/globe';
import '../../components/bottom-tabs';
import '../../components/info-accordion';
import type { LocationUpdateEvent } from '../../app-index';

// Define the custom event type

@customElement('app-map')
export class AppMap extends LitElement {
  private globeElement!: HTMLElement;
  private isForwarding = false;

  @state() private showAuroraOval = true;
  @state() private showNoaaOvation = false;
  @state() private showPolarElectroJet = false;
  @state() private isDeveloperMode = import.meta.env.DEV;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        height: 100vh;
        overflow: hidden;
        position: relative;
        background: #0D1726;
      }

      main {
        height: calc(100vh - 60px); /* Hardcoded height to avoid CSS variable issues */
        padding: 0;
        margin: 0;
        position: relative;
        background: #0D1726;
      }

      app-globe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
      }

      .info-container {
        position: absolute;
        top: calc(env(safe-area-inset-top) + 1rem);
        right: 1rem;
        z-index: 10;
        max-width: 400px;
      }

      @media (min-width: 768px) {
        .info-container {
          right: 1.5rem;
          top: calc(env(safe-area-inset-top) + 1.5rem);
        }
      }
    `
  ];

  firstUpdated() {
    // Get reference to the globe element
    this.globeElement = this.shadowRoot?.querySelector('app-globe') as HTMLElement;

    // Add event listener for location updates on window
    window.addEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Draw the aurora gradient canvas when NOAA Ovation is toggled on
    if (changedProperties.has('showNoaaOvation') && this.showNoaaOvation) {
      // Use setTimeout to ensure the canvas element is rendered
      setTimeout(() => this.drawAuroraGradient(), 0);
    }
  }

  private drawAuroraGradient() {
    const canvas = this.shadowRoot?.getElementById('aurora-colorbar') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = 240, h = 24;

    // Color stops (will be interpolated)
    const colorStops = [
      { v: 5, color: [39, 128, 151], a: 0.8 },
      { v: 20, color: [39, 128, 151], a: 0.8 },
      { v: 30, color: [24, 225, 211], a: 0.8 },
      { v: 40, color: [24, 225, 211], a: 0.8 },
      { v: 50, color: [255, 180, 115], a: 0.8 },
      { v: 60, color: [239, 91, 40], a: 0.8 },
      { v: 70, color: [239, 91, 40], a: 0.8 },
      { v: 90, color: [229, 0, 0], a: 0.8 },
      { v: 100, color: [229, 0, 0], a: 0.8 }
    ];

    // Build gradient using provided stops
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    const minStop = colorStops[0].v;
    const maxStop = colorStops[colorStops.length - 1].v;

    colorStops.forEach(s => {
      const pos = (s.v - minStop) / (maxStop - minStop);
      const [r, g, b] = s.color;
      grad.addColorStop(Math.max(0, Math.min(1, pos)), `rgba(${r},${g},${b},${s.a})`);
    });

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Draw border
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('location-update', this.handleLocationUpdate.bind(this) as EventListener);
  }

  private handleLocationUpdate(event: LocationUpdateEvent) {
    // Prevent infinite recursion by checking if we're already forwarding
    if (this.isForwarding) {
      return;
    }

    console.log('Map received location update:', event.detail);

    // Set forwarding flag
    this.isForwarding = true;

    // Forward the event to the globe component without bubbling
    this.globeElement?.dispatchEvent(new CustomEvent('location-update', {
      detail: event.detail,
      bubbles: false, // Prevent bubbling
      composed: true
    }));

    // Reset forwarding flag
    this.isForwarding = false;
  }

  private handleOvalToggle(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.showAuroraOval = checkbox.checked;

    // Dispatch event to globe component
    this.globeElement?.dispatchEvent(new CustomEvent('toggle-aurora-oval', {
      detail: { show: this.showAuroraOval },
      bubbles: false,
      composed: true
    }));
  }

  private handleNoaaOvationToggle(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.showNoaaOvation = checkbox.checked;

    // Dispatch event to globe component
    this.globeElement?.dispatchEvent(new CustomEvent('toggle-noaa-ovation', {
      detail: { show: this.showNoaaOvation },
      bubbles: false,
      composed: true
    }));
  }

  private handlePolarElectroJetToggle(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.showPolarElectroJet = checkbox.checked;

    // Dispatch event to globe component
    this.globeElement?.dispatchEvent(new CustomEvent('toggle-polar-electro-jet', {
      detail: { show: this.showPolarElectroJet },
      bubbles: false,
      composed: true
    }));
  }

  render() {
    return html`
      <main>
        <app-globe></app-globe>
        <div class="info-container">
          <info-accordion title="Info" storageKey="globe-info-expanded">
            <div style="margin-bottom: 1.5rem;">
              <h4 style="margin: 0 0 0.75rem 0; color: #40f9d1; font-size: 0.95rem;">${t('stationActivityLevel')}</h4>
              <table style="width: 100%; border-collapse: collapse; border: none;">
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center; width: 40px;">
                    <div style="width: 20px; height: 20px; background: #278097; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelQuiet')} (&lt; |70| nT)</td>
                </tr>
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center;">
                    <div style="width: 20px; height: 20px; background: #17E1D4; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelSome')} (|70-99| nT)</td>
                </tr>
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center;">
                    <div style="width: 20px; height: 20px; background: #FFB473; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelHigh')} (|100-249| nT)</td>
                </tr>
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center;">
                    <div style="width: 20px; height: 20px; background: #EF5B28; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelVeryHigh')} (|250-499| nT)</td>
                </tr>
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center;">
                    <div style="width: 20px; height: 20px; background: #E50000; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelExtreme')} (&ge; |500| nT)</td>
                </tr>
                <tr style="border: none;">
                  <td style="border: none; padding: 0.4rem 0.5rem 0.4rem 0; text-align: center;">
                    <div style="width: 20px; height: 20px; background: #666666; border-radius: 50%; margin: 0 auto;"></div>
                  </td>
                  <td style="border: none; padding: 0.4rem 0; color: #ffffff; font-size: 0.9rem;">${t('levelNoData')} </td>
                </tr>
              </table>
            </div>

            <div>
              <h4 style="margin: 0 0 0.75rem 0; color: #40f9d1; font-size: 0.95rem;">${t('showAuroraOvals')}</h4>
              <label style="display: flex; align-items: center; cursor: pointer; user-select: none; margin-bottom: 0.5rem;">
                <input
                  type="checkbox"
                  .checked=${this.showAuroraOval}
                  @change=${this.handleOvalToggle}
                  style="margin-right: 0.5rem; cursor: pointer; width: 16px; height: 16px;"
                />
                <span style="color: #ffffff; font-size: 0.9rem;">Feldstein-Starkov model</span>
              </label>
              <label style="display: flex; align-items: center; cursor: pointer; user-select: none; margin-bottom: 0.5rem;">
                <input
                  type="checkbox"
                  .checked=${this.showNoaaOvation}
                  @change=${this.handleNoaaOvationToggle}
                  style="margin-right: 0.5rem; cursor: pointer; width: 16px; height: 16px;"
                />
                <span style="color: #ffffff; font-size: 0.9rem;">NOAA OVATION model</span>
              </label>
              ${this.showNoaaOvation ? html`
                <div style="padding-top: 0.15rem; padding-bottom:0.75rem;">
                  <div style="display: flex; flex-direction: column; align-items: center;">
                    <canvas id="aurora-colorbar" width="200" height="24" style="display: block;"></canvas>
                    <div style="display: flex; justify-content: space-between; width: 200px; margin-top: 6px; font-size: 0.75rem; color: rgba(255, 255, 255, 0.8);">
                      <span>3 %</span>
                      <span>100 %</span>
                    </div>
                  </div>
                </div>
              ` : ''}
              ${this.isDeveloperMode ? html`
                <label style="display: flex; align-items: center; cursor: pointer; user-select: none; margin-bottom: 0.5rem;">
                  <input
                    type="checkbox"
                    .checked=${this.showPolarElectroJet}
                    @change=${this.handlePolarElectroJetToggle}
                    style="margin-right: 0.5rem; cursor: pointer; width: 16px; height: 16px;"
                  />
                  <span style="color: #ffffff; font-size: 0.9rem;">Polar electro jet</span>
                </label>
              ` : ''}
            </div>
          </info-accordion>
        </div>
      </main>
      <bottom-tabs></bottom-tabs>
    `;
  }
}